import React, { useEffect, useMemo, useState } from "react";
import * as S from "./Billing.style";
import { useTranslation } from "react-i18next";

// GraphQL
import { useQuery } from "@apollo/client";
import { QUERY_GET_COMPLETE_DOCTOR_DATA, QUERY_GET_DOCTORS_DATA } from "../../../../../graphql/queries/Doctor";

// components
import ZaiaReaction from "../../../../zaiaComponents/zaiaReaction";

// pipes
import * as P from "../../../../../utils/pipes";
import { QUERY_GET_DOCTOR_BILLING_BY_DATES, QUERY_GET_DOCTOR_BILLING_BY_DATES_COUNT } from "../../../../../graphql/queries/DoctorBilling";
import FlorenzButton from "../../../../florenzComponents/florenzButton";
import BillingTable from "./components/billingTable";
import { QUERY_GET_PROCESS_TYPES } from "../../../../../graphql/queries/Process";
import useDoctorData from "../../../../../hooks/useDoctorData";

// assets
const zaiaError = `${process.env.REACT_APP_STORAGE_ZAIA_RESOURCES}/zaiaReactions/reaction-error-data.svg`;
const zaiaLoad = `${process.env.REACT_APP_STORAGE_ZAIA_RESOURCES}/zaiaReactions/singularity-health-loader.svg`;
const ENV = process.env.REACT_APP_ENV;

function Billing() {
  const {t, i18n: { language } } = useTranslation("global");

  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(null);

  const [monthSelected, setMonthSelected] = useState(null);

  const [billingList, setBillingList] = useState([]);

  const handleDownloadCSV = () => {
    if (billingList.length === 0) return;

    const headers = [
      t("billing.patientId"),
      t("billing.patientName"),
      t("billing.daysOfData"),
      t("billing.minutesLogged"),
      t("billing.nurseStaff"),
      t("billing.nurseStaffId"),
      t("billing.description"),
      t("billing.provider")
    ];

    const rows = billingList.map(b => [
      b.userId,
      b.patientName,
      b.daysOfData,
      b.durationLogged,
      b.doctorName,
      b.doctorId,
      b.description,
      b.provider
    ]);

    // 🧩 Usar punto y coma (;) como separador para Excel
    const separator = ";";

    // 🧩 Agregar BOM para compatibilidad con Excel (UTF-8 con acentos)
    const BOM = "\uFEFF";

    const csvContent = [
      headers.join(separator),
      ...rows.map(r => r.map(value => `"${(value ?? "").toString().replace(/"/g, '""')}"`).join(separator))
    ].join("\n");

    const blob = new Blob([BOM + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `billing_${year}_${month + 1}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const {doctorData} = useDoctorData();
    const { data: processTypes } = useQuery(QUERY_GET_PROCESS_TYPES);
  
    const { 
      data: doctorsDataRpmFlorida,
      loading: loadingDoctorsDataRpmFlorida,
      error: errorDoctorsDataRpmFlorida,
    } = useQuery(
      QUERY_GET_DOCTORS_DATA,
      {
        variables: {
          doctorId: doctorData?.id,
          processId: ENV === "production" ? 73 : processTypes?.process[0]?.id 
        }
      }
    );

    const { 
      data: doctorsDataTowerUrology,
      loading: loadingDoctorsDataTowerUrology,
      error: errorDoctorsDataTowerUrology,
    } = useQuery(
      QUERY_GET_DOCTORS_DATA,
      {
        variables: {
          doctorId: doctorData?.id,
          processId: ENV === "production" ? 50 : processTypes?.process[0]?.id 
        },
        skip: ENV !== "production"
      }
    );
  
    const doctorIds = useMemo(() => {
      const rmpFloridaDoctorsIds = doctorsDataRpmFlorida?.health_system_colleagues_by_process.health_system_colleagues[0].doctors.map((doctor) => doctor.id) ||[];
      let towerUrologyDoctorsIds = [];
      if(ENV === "production") {
        towerUrologyDoctorsIds = doctorsDataTowerUrology?.health_system_colleagues_by_process.health_system_colleagues[0].doctors.map((doctor) => doctor.id) ||[];
      }
      
      return [...rmpFloridaDoctorsIds, ...towerUrologyDoctorsIds, doctorData?.id];
    }, [doctorsDataRpmFlorida, doctorsDataTowerUrology, doctorData]);  
  

  // if (doctorBillingLoading) 
  //   return (
  //     <S.BillingDiv>
  //       <ZaiaReaction
  //         widthimg="150px"
  //         zaiaImg={zaiaLoad}
  //       />  
  //     </S.BillingDiv>
  //   );

  // if (doctorBillingLoading) 
  //   return (
  //     <S.BillingDiv>
  //       <ZaiaReaction
  //         zaiaImg={zaiaError}
  //         widthimg="125px"
  //         text={t("profile.errorInfo")}
  //         sizeText="17px"
  //         widthText="240px"
  //       />
  //     </S.BillingDiv>
  //   );  

  return (
    <S.BillingDiv>
      {
        month === null &&
        <div className="billing-selection">
          <div className="title">
            {
              t("billing.selectBillingPeriod")
            }
          </div>
          <div className="year-selection">
            <FlorenzButton
              typeStyle={"outline"}
              size="s"
              icon={"zaia-icono-atras"}
              iconSize={"10px"}
              onClick={() => {
                setYear(year - 1)
                setMonthSelected(null)
              }}
            />
            <div className="year-selection__year">
              {year}
            </div>
            <FlorenzButton
              typeStyle={"outline"}
              size="s"
              icon={"zaia-i-next"}
              iconSize={"10px"}
              onClick={() => {
                setYear(year + 1)
                setMonthSelected(null)
              }}
            />
          </div>
          <div className="billing-info">
            <div className="billing-info__month-selection">
              {
                [0,1,2,3,4,5,6,7,8,9,10,11].map((m, index) => (
                  <BillingMonth 
                    key={index} 
                    month={m} 
                    year={year} 
                    selected={monthSelected === index} 
                    onSelected={setMonthSelected}
                    doctorIds={doctorIds}
                  />
                ))
              }
            </div>
          </div>
        </div>
      }
      {
        month !== null &&
        <div className="billing-info">
          <BillingTable 
            month={month} 
            year={year} 
            setBillingList={setBillingList}
            doctorIds={doctorIds}
          />
        </div>
      }
      <div className="footer">
        <div className="footer__left">
          {
            month !== null &&
            <div className="button-period"
              onClick={
                () => setMonth(null)
              }
            >
              <div className="button-period__close">
                <i className="icon zaia-icono-cerrar"></i>
              </div>
              <div className="button-period__text">
                {t("billing.billingPeriod")} <b>{P.nameMonth(month, "L", t)} {year}</b>
              </div>
            </div>
          }
        </div>
        <div className="footer__right">
          {
            month === null && 
            <FlorenzButton
              typeStyle={"primary"}
              size="s"
              icon={monthSelected === null && "zaia-i-lock-chat"}
              text={t("globally.continue")}
              disabled={monthSelected === null}
              widthComplete={true}
              onClick={() => {
                monthSelected !== null && setMonth(monthSelected);
              }}
            />
          }
          {
            month !== null && 
            <FlorenzButton
              typeStyle={"secondary"}
              size="s"
              icon={"zaia-i-download"}
              text={t("globally.download")}
              widthComplete={true}
              onClick={() => {
                billingList.length > 0 && handleDownloadCSV();
              }}
            />
          }
        </div>
      </div>
    </S.BillingDiv>
  );
}

function BillingMonth({month, year, selected, onSelected, doctorIds}) {
  
  const {t, i18n: { language } } = useTranslation("global");

    const { 
      data: doctorBillingCountData, 
      loading: doctorBillingCountLoading, 
      error: doctorBillingCountError
    } = useQuery(
      QUERY_GET_DOCTOR_BILLING_BY_DATES_COUNT, 
      { 
        variables: { 
          initialDate: new Date(year, month, 1, 0, 0, 0, 0),
          finalDate: new Date(year, month + 1, 0, 23, 59, 59, 999),
          doctorsIds: doctorIds
        },
        skip: month === null
      },
    );


    console.log("in the summertime", doctorBillingCountData);
    


  return (
    <S.BillingMonth 
      selected={selected}
      disabled={doctorBillingCountData?.doctorBilling_aggregate.aggregate.count === 0} 
      onClick={() => {
        doctorBillingCountData?.doctorBilling_aggregate.aggregate.count > 0 && onSelected(month)
      }}
    >
      <div className="month-name">
        {P.nameMonth(month, "L", t)}  
      </div>    
      <div className="month-subtitle">
        <div>
          {
            doctorBillingCountData?.doctorBilling_aggregate.aggregate.count === 0 && "Sin reporte"
          }
        </div>
        {
          selected && "Periodo seleccionado"
        }
      </div>
    </S.BillingMonth>
  );
}

export default Billing;
